import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import useRevealObserver from './hooks/useRevealObserver';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import CustomCursor from './components/CustomCursor/CustomCursor';
import PageLoader from './components/PageLoader/PageLoader';
import ScrollProgress from './components/ScrollProgress/ScrollProgress';
import QuickEnquiry from './components/QuickEnquiry/QuickEnquiry';
import { AppContextProvider } from './context/AppContext';

// Pages
import Home       from './pages/Home/Home';
import About      from './pages/About/About';
import Courses    from './pages/Courses/Courses';
import Admissions from './pages/Admissions/Admissions';
import Gallery    from './pages/Gallery/Gallery';
import Contact    from './pages/Contact/Contact';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminLogin from './pages/Admin/AdminLogin';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import TermsOfUse    from './pages/TermsOfUse/TermsOfUse';
import ThankYou      from './pages/ThankYou/ThankYou';
import NotFound      from './pages/NotFound/NotFound';

// Styles
import './index.css';

/* Scroll to top + re-observe reveal elements on route change */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useRevealObserver();
  return null;
};

/* Floating WhatsApp button */
const WhatsApp = () => (
  <a
    href="https://wa.me/919960555941"
    target="_blank"
    rel="noreferrer"
    style={{
      position:'fixed', bottom:28, right:28, zIndex:9000,
      width:56, height:56, borderRadius:'50%',
      background:'#25D366', color:'#fff',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:26, boxShadow:'0 6px 24px rgba(37,211,102,0.35)',
      textDecoration:'none',
      animation:'waPulse 3s ease infinite',
    }}
    aria-label="Chat on WhatsApp"
  >
    <svg 
      viewBox="0 0 448 512" 
      width="30" 
      height="30" 
      fill="currentColor"
    >
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.7 17.7 69.4 27 106.1 27 122.4 0 222-99.6 222-222 0-59.3-23.2-115-65.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.2-8.5-44.2-27.1-16.4-14.6-27.4-32.7-30.6-38.2-3.2-5.6-.3-8.6 2.5-11.3 2.5-2.5 5.6-6.5 8.3-9.7 2.8-3.3 3.7-5.6 5.6-9.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.2 5.8 23.5 9.2 31.5 11.8 13.3 4.2 25.4 3.6 35 2.2 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
    </svg>
  </a>
);

function App() {
  return (
    <AppContextProvider>
      <BrowserRouter>
        <ScrollProgress />
        <CustomCursor />
        <PageLoader />
        <ScrollToTop />
        <div className="app">
          <Navbar />
          <main>
            <Routes>
              <Route path="/"              element={<Home />} />
              <Route path="/about"         element={<About />} />
              <Route path="/courses"       element={<Courses />} />
              <Route path="/admissions"    element={<Admissions />} />
              <Route path="/gallery"       element={<Gallery />} />
              <Route path="/contact"       element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use"   element={<TermsOfUse />} />
              <Route path="/admin/login"   element={<AdminLogin />} />
              <Route path="/admin"         element={<AdminDashboard />} />
              <Route path="/thank-you"     element={<ThankYou />} />
              <Route path="/error"         element={<NotFound />} />
              <Route path="*"              element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
        <WhatsApp />
        <QuickEnquiry />
      </BrowserRouter>
      <style>{`
        @keyframes waPulse {
          0%,100%{ box-shadow:0 6px 24px rgba(37,211,102,0.35); }
          50%     { box-shadow:0 6px 32px rgba(37,211,102,0.65); }
        }
      `}</style>
    </AppContextProvider>
  );
}

export default App;
